import { useMemo, useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  ButtonBase,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@material-ui/core';
import _ from 'lodash';
import { dutyAssignAPI } from '../../../../../apis';
import { ArrowLeftIcon, CheckIcon } from '../../../../../components/Icon';
import { classNames, onKeyUpEnterOrSpace } from '../../../../../utils';
import { i18n } from '../../../../../language';

const useStyles = makeStyles((theme) => ({
  root: {},
  toolbar: {
    height: '4.063rem',
    display: 'flex',
    alignItems: 'center',
    background: '#fff',
    color: '#538fff',
    padding: '0 3.437rem',
    boxShadow: '0 1px 0 0 #e4e7eb',
    fontFamily: 'NotoSans-Medium',
    borderBottom: 'solid 1px #e4e7eb',
  },
  arrowLeftIcon: {
    fontSize: '1rem',
    fontFamily: 'NotoSans-Medium',
  },
  back: {
    paddingLeft: '1rem',
    fontSize: '0.875rem',
    fontWeight: '500',
    lineHeight: '1.43',
  },
  body: {
    height: 'calc(100vh - 8.438rem)',
    backgroundColor: theme.palette.font.white,
    padding: '1.875rem 3.438rem 0',
  },
  title: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '2.25rem',
    color: '#001d63',
    fontWeight: 'bold',
    marginBottom: '3.125rem',
  },
  dialogTitle: {
    backgroundColor: theme.palette.font.white,
    fontFamily: 'NotoSans-Bold',
    fontSize: '0.875rem',
    lineHeight: '1.875rem',
    letterSpacing: 0,
    color: theme.palette.primary.main,
    fontWeight: 'bold',
    border: 'none',
  },
  role: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1.125rem',
    lineHeight: '1.875rem',
    letterSpacing: 0,
    color: theme.palette.font.main,
    fontWeight: '500',
  },
  row: {
    borderTop: 'solid 1px #e4e7eb',
    borderBottom: 'solid 1px #e4e7eb',
    borderRight: '0',
    borderLeft: '0',
  },
  box: {
    width: '1.5rem',
    height: '1.5rem',
    minWidth: '1.5rem',
    minHeight: '1.5rem',
    borderRadius: '5px',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  checkbox: {
    border: 'solid 1px #c0c6cb',
  },
  checkedbox: ({ color }) => ({
    backgroundColor: color ?? '#28d2ab',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }),
  checkIcon: {
    color: theme.palette.font.white,
    fontSize: '0.813rem',
  },
  foot: {
    marginTop: '3.313rem',
    display: 'flex',
    paddingLeft: '1.25rem',
  },
  content: {
    marginLeft: '0.938rem',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1.125rem',
    lineHeight: '1.875rem',
    letterSpacing: 0,
    color: theme.palette.font.main,
    fontWeight: '500',
  },
  checkboxBody: {
    display: 'flex',
    alignItems: 'center',
  },
  unCheck: {
    backgroundColor: '#c0c6cb',
    display: 'grid',
    placeItems: 'center',
  },
}));

const Checkbox = ({ check, handleChange, open = true, className, id, color }) => {
  const classes = useStyles({ color });

  const checkBoxClassName = check
    ? classNames(classes.box, classes.checkedbox, className)
    : classNames(classes.box, classes.checkbox, className);

  return open ? (
    <div
      className={checkBoxClassName}
      id={id}
      onClick={handleChange}
      tabIndex={0}
      onKeyUp={(e) => onKeyUpEnterOrSpace(e, handleChange)}
    >
      {check && <CheckIcon className={classes.checkIcon} />}
    </div>
  ) : (
    <div className={classNames(classes.box, classes.unCheck)} />
  );
};

const Setting = ({ history }) => {
  const classes = useStyles();
  const [rolePermissions, setRolePermissions] = useState();
  const [loginYn, setLoginYn] = useState(false);
  const [confirmYn, setConfirmYn] = useState(false);
  const [quickYn, setQuickYn] = useState(false);

  // get setting detail API
  const getSettingAPI = async () => {
    try {
      const { result } = await dutyAssignAPI.getSettingDetail();
      setRolePermissions(result);
      setLoginYn(result.loginYn === 1);
      setConfirmYn(result.confirmYn === 1);
      setQuickYn(result.quickYn === 1);
    } catch (error) {
      console.log('-----Get Setting Detail error----->', error);
    }
  };

  // save setting detail API
  const saveSettingAPI = async (props, login, confirm, quick) => {
    try {
      await dutyAssignAPI.postSaveAttendanceSetting({
        icYn: props[0] ? 1 : 0,
        nurseYn: props[1] ? 1 : 0,
        clerkYn: props[2] ? 1 : 0,
        loginYn: login ? 1 : 0,
        confirmYn: confirm ? 1 : 0,
        quickYn: quick ? 1 : 0,
      });
      await getSettingAPI();
    } catch (error) {
      console.log('-----Get Setting Detail error----->', error);
    }
  };

  useEffect(() => {
    (async () => {
      await getSettingAPI();
    })();
  }, []);

  const data = useMemo(() => {
    let list = [{ role: 'IC' }, { role: 'Nurse' }, { role: 'Supporting Staff' }];

    if (rolePermissions) {
      list[0].permissions = rolePermissions['icYn'] === 1;
      list[1].permissions = rolePermissions['nurseYn'] === 1;
      list[2].permissions = rolePermissions['clerkYn'] === 1;
    }

    return list;
  }, [rolePermissions]);

  const backHandle = () => {
    history.goBack(1);
  };

  return (
    <div className={classes.root}>
      <div className={classes.toolbar}>
        <ButtonBase id="1" onClick={backHandle}>
          <ArrowLeftIcon className={classes.arrowLeftIcon} />
          <Typography className={classes.back}>{i18n.t('DUTYASSIGN_BACK')}</Typography>
        </ButtonBase>
      </div>
      <div className={classes.body}>
        <div className={classes.title}>{i18n.t('DUTYASSIGN_SETTING')}</div>
        <TableContainer className={classes.setting}>
          <Table stickyHeader style={{ borderCollapse: 'collapse', width: 'calc(100% - 2px)' }}>
            <TableHead>
              <TableRow>
                <TableCell className={classes.dialogTitle}>{i18n.t('DUTYASSIGN_ATTENDANCE_TABLE_RANK')}</TableCell>
                <TableCell className={classes.dialogTitle}>{i18n.t('DUTYASSIGN_SHOW_TIME')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rolePermissions &&
                data.map((item, index) => {
                  return (
                    <TableRow className={classes.row} key={item.role}>
                      <TableCell className={classes.role}>{item.role}</TableCell>
                      <TableCell style={{ paddingLeft: '2.6rem' }}>
                        <Checkbox
                          id={`dutyassign-attendance-setting-${index}`}
                          check={item.permissions}
                          handleChange={() => {
                            let state = [];
                            state = _.map(data, (role) => {
                              return role.permissions;
                            });
                            state[index] = !state[index];
                            (async () => {
                              await saveSettingAPI(state, loginYn, confirmYn, quickYn);
                            })();
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        <div className={classes.foot}>
          <div className={classes.checkboxBody}>
            <Checkbox
              check={loginYn}
              handleChange={() => {
                let state = [];
                state = _.map(data, (role) => {
                  return role.permissions;
                });
                (async () => {
                  await saveSettingAPI(state, !loginYn, confirmYn, quickYn);
                })();
              }}
              id="Duty-div-abo"
            />
          </div>
          <div className={classes.content}>{i18n.t('DUTYASSIGN_ATTENDANCE_SETTING_CONFIRM_LOGIN')}</div>
        </div>
        {!loginYn && (
          <div className={classes.foot}>
            <div className={classes.checkboxBody}>
              <Checkbox
                style={{ color: loginYn ? '#999' : null }}
                check={confirmYn}
                handleChange={() => {
                  let state = [];
                  state = _.map(data, (role) => {
                    return role.permissions;
                  });
                  (async () => {
                    if (!loginYn) {
                      await saveSettingAPI(state, loginYn, !confirmYn, quickYn);
                    }
                  })();
                }}
                id="Duty-div-con"
              />
            </div>
            <div className={classes.content} style={{ color: loginYn ? '#999' : null }}>
              {i18n.t('DUTYASSIGN_ATTENDANCE_SETTING_CONFIRM_TITLE')}
            </div>
          </div>
        )}

        <div className={classes.foot}>
          <div className={classes.checkboxBody}>
            <Checkbox
              check={quickYn}
              handleChange={() => {
                let state = [];
                state = _.map(data, (role) => {
                  return role.permissions;
                });
                (async () => {
                  await saveSettingAPI(state, loginYn, confirmYn, !quickYn);
                })();
              }}
              id="Duty-div-abo"
            />
          </div>
          <div className={classes.content}>{i18n.t('DUTYASSIGN_ATTENDANCE_CLOCK_ALLOW')}</div>
        </div>
      </div>
    </div>
  );
};

export default Setting;

export { Setting, Checkbox };
